import React, { useState } from 'react';
import Button from '../../../../shared/button';
import Modal from '../../../../shared/modal';
import Error from '../../../../shared/error';
import PasswordInput from '../../../../shared/input/passwordInput';
import { useGetCurrentUserQuery, useNewPasswordMutation } from '../../../../store/user/userApi';

interface IModelsModal {
    openModal: boolean;
    handleCloseModal: () => void;
    onShowMessage: (type: string, message: string) => void;
}

const ResetPasswordModal = ({
    openModal,
    handleCloseModal,
    onShowMessage
}: IModelsModal) => {
    const { data: userData } = useGetCurrentUserQuery();
    const [setNewPassword] = useNewPasswordMutation();
    const [submitError, setSubmitError] = useState<string | undefined>();
    const [loadingSave, setLoadingSave] = useState<boolean>(false);
    const [password, setPassword] = useState<string | undefined>();
    const [repeatPassword, setRepeatPassword] = useState<string | undefined>();

    const onCloseModal = () => {
        setSubmitError(undefined);
        setLoadingSave(false);
        setPassword(undefined);
        setRepeatPassword(undefined);
        handleCloseModal();
    };

    const onChangeInputs = (value: string | null | undefined, type: string) => {
        switch (type) {
            case 'password':
                setPassword(value || '');
                break;
            case 'repeatPassword':
                setRepeatPassword(value || '');
                break;
            default:
                break;
        }
    };

    const passwordsMatch = (repeatPassword?.length ?? 0) > 0 && repeatPassword === password;

    const repeatHelperText = !repeatPassword
        ? 'Password must match'
        : passwordsMatch
            ? 'Passwords match'
            : 'Passwords do not match';

    const onClickSaveNewPassword = async () => {
        const trim = (v?: string | null) => (v ?? '').trim();
        const _password = trim(password);
        const _repeat = trim(repeatPassword);
        const username = userData?.data?.username;

        if (!_password || !_repeat) {
            setSubmitError('Please fill in both password fields.');
            return;
        }

        const passRe = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{6,}$/;
        if (!passRe.test(_password)) {
            setSubmitError('Password must include 6+ characters and at least 1 uppercase letter, 1 number, and 1 symbol.');
            return;
        }

        if (_password !== _repeat) {
            setSubmitError('Passwords must match.');
            return;
        }

        try {
            setSubmitError(undefined);
            setLoadingSave(true);

            const payload = {
                username: username,
                password: _password
            };

            await setNewPassword(payload).unwrap();
            onShowMessage('success', 'Your password has been updated.');
            onCloseModal();
        } catch (err: any) {
            const apiError = err?.data?.message || `${err}` || 'Failed to reset password.';
            setSubmitError(apiError);
            setLoadingSave(false);
        }
    };

    const blockClipboard = (e: React.ClipboardEvent<HTMLDivElement>) => {
        e.preventDefault();
    };

    return (
        <Modal
            open={openModal}
            title='Reset Password'
            headerStyle='border-b-1 py-3 mb-4'
            onClose={onCloseModal}
            contentContainerStyle='!rounded-xl min-w-[95%] md:min-w-[60%] lg:min-w-[50%]'>
            <div className='p-4 sm:p-6 bg-white dark:bg-bgColor-darkMode'>
                <p className='text-sm text-gray-600 dark:text-gray-300 mb-4'>
                    Please enter your new password and confirm it.
                </p>

                <div
                    className='space-y-2'
                    onCopy={blockClipboard}
                    onPaste={blockClipboard}
                    onCut={blockClipboard}
                >
                    <PasswordInput
                        label='New password'
                        placeholder=''
                        showValue={true}
                        inputValue={password}
                        inputStyle='!rounded-xl'
                        labelStyle='!text-black dark:!text-white !text-sm'
                        inputUniqueName='password'
                        containerStyle='!mb-3'
                        onChangeInput={onChangeInputs}
                        helperText='Password must include 6 characters or more and at least 1 uppercase letter, 1 number and 1 symbol'
                    />

                    <PasswordInput
                        label='Confirm new password'
                        placeholder=''
                        showValue={true}
                        inputValue={repeatPassword}
                        inputStyle='!rounded-xl'
                        labelStyle='!text-black dark:!text-white !text-sm'
                        inputUniqueName='repeatPassword'
                        containerStyle='!mb-1'
                        onChangeInput={onChangeInputs}
                        helperText={repeatHelperText}
                        error={!passwordsMatch && !!repeatPassword ? 'Passwords do not match' : ''}
                    />
                </div>

                {submitError && <Error text={submitError} />}

                <div className='flex justify-end mt-6'>
                    <Button
                        label={loadingSave ? 'Saving...' : 'Save New Password'}
                        className={`btn-primary-rounded-less ${loadingSave ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClickButton={() => !loadingSave && onClickSaveNewPassword()}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default ResetPasswordModal;
